import { Radio, Music, ChevronUp, ChevronDown, Play, Pause, SkipForward, X, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

type Zone = {
  id: string;
  name: string;
  description: string;
  playlist_id: string | null;
  latitude: number;
  longitude: number;
};

type PlaylistTrack = {
  id: string;
  youtube_url: string;
  title: string;
  position: number;
};

type ZoneBottomPanelProps = {
  zone: Zone;
  onClose: () => void;
  onPlayTrack?: (youtubeUrl: string) => void;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  currentTrackUrl?: string;
  onAddSoundDrop?: (lat: number, lng: number) => void;
};

export function ZoneBottomPanel({
  zone,
  onClose,
  onPlayTrack,
  isPlaying = false,
  onTogglePlay,
  currentTrackUrl,
  onAddSoundDrop
}: ZoneBottomPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);

  useEffect(() => {
    if (zone.playlist_id) {
      fetchTracks();
    } else {
      setLoading(false);
    }
  }, [zone.playlist_id]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  useEffect(() => {
    if (currentTrackUrl && tracks.length > 0) {
      const index = tracks.findIndex(t => t.youtube_url === currentTrackUrl);
      if (index !== -1) {
        setCurrentTrackIndex(index);
      }
    }
  }, [currentTrackUrl, tracks]);

  const fetchTracks = async () => {
    if (!zone.playlist_id) return;

    try {
      const { data, error } = await supabase
        .from('playlist_tracks')
        .select('*')
        .eq('playlist_id', zone.playlist_id)
        .order('position', { ascending: true });

      if (error) throw error;
      setTracks(data || []);

      if (data && data.length > 0 && onPlayTrack) {
        onPlayTrack(data[0].youtube_url);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNextTrack = () => {
    if (tracks.length === 0) return;
    const nextIndex = (currentTrackIndex + 1) % tracks.length;
    setCurrentTrackIndex(nextIndex);
    if (onPlayTrack) {
      onPlayTrack(tracks[nextIndex].youtube_url);
    }
  };

  const currentTrack = tracks[currentTrackIndex];

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 shadow-2xl z-40 transition-all duration-300 ${
        isExpanded ? 'h-96' : 'h-24'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 h-full flex flex-col">
        <div className="flex items-center justify-between py-4 flex-shrink-0">
          <div className="flex items-center gap-4 flex-1">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Radio className="w-6 h-6 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xs text-gray-400 font-medium">Currently in Zone</p>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              </div>
              <p className="text-lg font-bold text-white truncate">{zone.name}</p>
              {currentTrack && (
                <p className="text-sm text-gray-300 truncate">{currentTrack.title}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {tracks.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={onTogglePlay}
                  className="w-10 h-10 bg-[#00c7b7] hover:bg-[#14f0d5] rounded-full flex items-center justify-center transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-5 h-5 text-white" />
                  ) : (
                    <Play className="w-5 h-5 text-white ml-0.5" />
                  )}
                </button>

                <button
                  onClick={handleNextTrack}
                  className="w-10 h-10 bg-gray-700 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
                >
                  <SkipForward className="w-5 h-5 text-gray-300" />
                </button>
              </div>
            )}

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-300" />
              ) : (
                <ChevronUp className="w-5 h-5 text-gray-300" />
              )}
            </button>

            <button
              onClick={onClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-gray-300" />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="flex-1 overflow-y-auto pb-4">
            {onAddSoundDrop && (
              <button
                onClick={() => onAddSoundDrop(zone.latitude, zone.longitude)}
                className="w-full flex items-center justify-center gap-2 p-3 mb-4 bg-[#00c7b7] hover:bg-[#14f0d5] text-white rounded-lg transition-colors font-medium"
              >
                <Plus className="w-5 h-5" />
                Add Sound Drop to this Zone
              </button>
            )}
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00c7b7]"></div>
              </div>
            ) : tracks.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <Music className="w-4 h-4 text-[#00c7b7]" />
                  <h3 className="font-semibold text-white">Playlist ({tracks.length} tracks)</h3>
                </div>
                {tracks.map((track, index) => (
                  <button
                    key={track.id}
                    onClick={() => {
                      setCurrentTrackIndex(index);
                      if (onPlayTrack) {
                        onPlayTrack(track.youtube_url);
                      }
                    }}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      index === currentTrackIndex
                        ? 'bg-gray-700 border border-[#00c7b7]'
                        : 'hover:bg-gray-700 border border-transparent'
                    }`}
                  >
                    <span className={`text-sm font-medium w-6 ${
                      index === currentTrackIndex ? 'text-[#00c7b7]' : 'text-gray-400'
                    }`}>
                      {index + 1}.
                    </span>
                    <div className="flex-1 text-left">
                      <p className={`font-medium ${
                        index === currentTrackIndex ? 'text-white' : 'text-gray-300'
                      }`}>
                        {track.title}
                      </p>
                    </div>
                    {index === currentTrackIndex && isPlaying && (
                      <div className="flex gap-1">
                        <span className="w-1 h-4 bg-[#00c7b7] rounded-full animate-pulse"></span>
                        <span className="w-1 h-4 bg-[#00c7b7] rounded-full animate-pulse delay-75"></span>
                        <span className="w-1 h-4 bg-[#00c7b7] rounded-full animate-pulse delay-150"></span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <Music className="w-12 h-12 mb-2" />
                <p>No tracks in this zone's playlist</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
